import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, User, Tag } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?published_only=true`);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories/list`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const filteredPosts = selectedCategory === "all" 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="insights-page">
      <SEOHead 
        title="Insights"
        description="Strategic insights on authority infrastructure, business transformation, and building enterprise value from Tessa Authority Systems."
        url="https://tessaauthority.com/insights"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12" data-testid="insights-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Insights & Perspectives
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Strategic<br />
              <span className="text-primary">Thinking</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              Frameworks, perspectives, and strategic insights on building authority infrastructure 
              and enterprise value from expertise-based businesses.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      {categories.length > 0 && (
        <section className="px-6 md:px-12 pb-12 border-t border-white/5 pt-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 text-xs font-medium tracking-[0.15em] uppercase transition-colors ${
                  selectedCategory === "all"
                    ? "bg-primary text-primary-foreground"
                    : "border border-white/10 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
                data-testid="category-filter-all"
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-xs font-medium tracking-[0.15em] uppercase transition-colors ${
                    selectedCategory === category
                      ? "bg-primary text-primary-foreground"
                      : "border border-white/10 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                  data-testid={`category-filter-${category}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts Grid */}
      <section className="py-12 md:py-24 px-6 md:px-12" data-testid="insights-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Loading insights...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="text-muted-foreground mb-4">No insights published yet.</p>
              <p className="text-sm text-muted-foreground/70">
                Check back soon for strategic perspectives and frameworks.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group"
                  data-testid={`insight-card-${index}`}
                >
                  <Link to={`/insights/${post.slug}`}>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.created_at)}
                        </span>
                      </div>
                      <h2 className="font-serif text-2xl md:text-3xl font-normal text-foreground group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-base font-light leading-relaxed text-muted-foreground line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-primary text-sm font-medium">
                        Read More
                        <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightsPage;
